import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Divider, Link, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import KitChip from '../../components/ui/Chip';
import { formatDay, formatWindow } from './exploreFormat';

const VISIBLE_ENTRIES = 3;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// An event's day or days without the year: "15 Sep", "6–9 Aug", "29 Sep – 3 Oct".
function eventDays(start, end) {
    const [, sm, sd] = String(start).split('-').map(Number);
    const [, em, ed] = String(end || start).split('-').map(Number);
    if (!sm || !sd) return '';
    if (!em || (sm === em && sd === ed)) return `${sd} ${MONTHS[sm - 1]}`;
    if (sm === em) return `${sd}–${ed} ${MONTHS[sm - 1]}`;
    return `${sd} ${MONTHS[sm - 1]} – ${ed} ${MONTHS[em - 1]}`;
}

// Each citation once, in order, across the shown entries.
function distinctSources(entries) {
    const seen = new Set();
    const sources = [];
    entries.forEach((entry) => (entry.sources || []).forEach((source) => {
        const key = `${source.url || ''}|${source.title || ''}|${source.publisher || ''}`;
        if (seen.has(key)) return;
        seen.add(key);
        sources.push(source);
    }));
    return sources;
}

const emptyText = { fontSize: '0.75rem', textAlign: 'center', py: 2.5 };

/**
 * What was happening in markets during a period: the published calendar
 * entries that overlap it, with dated one-line events, tags and a sources
 * line that reads without its links (dead links keep title, publisher and
 * date). Laid out like the Watchlist news popover. With `leadInBefore` (the
 * period's start, when the read also covers the month before), entries that
 * end before it are marked as the lead-in.
 */
export default function CalendarCard({ windowLabel, leadInBefore = null, loading = false, error = null, calendar = null, onRetry }) {
    const [expanded, setExpanded] = useState(null);
    const entries = calendar?.entries || [];
    const showAll = expanded === windowLabel;
    const shown = showAll ? entries : entries.slice(0, VISIBLE_ENTRIES);
    const sources = distinctSources(shown);
    let body;
    if (loading) {
        body = <Typography variant="body2" color="text.secondary" sx={emptyText}>Loading the market calendar…</Typography>;
    } else if (error) {
        body = (
            <Box sx={{ ...emptyText, display: 'grid', gap: 1, justifyItems: 'center' }}>
                <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem' }}>Couldn’t load the market calendar.</Typography>
                {onRetry && <Button size="small" onClick={onRetry}>Retry</Button>}
            </Box>
        );
    } else if (!calendar?.available) {
        body = (
            <Typography variant="body2" color="text.secondary" sx={emptyText}>
                The market calendar isn’t available yet. Once it is published, each period shows what happened in markets, with sources.
            </Typography>
        );
    } else if (!entries.length) {
        body = <Typography variant="body2" color="text.secondary" sx={emptyText}>No calendar entries cover these dates yet.</Typography>;
    } else {
        body = shown.map((entry, index) => (
            <Box key={entry.entry_id ?? `${entry.period_start}:${entry.granularity}`} component="section" aria-label={entry.title}
                sx={{ py: 0.75, ...(index > 0 ? { borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.5)}` } : {}) }}>
                <Typography component="h5" sx={{ m: 0, fontSize: '0.78rem', fontWeight: 600, lineHeight: 1.35 }}>{entry.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {entry.granularity === 'week' ? `Week of ${formatDay(entry.period_start)}` : formatWindow(entry.period_start, entry.period_end)}
                    {leadInBefore && entry.period_end < leadInBefore ? ' · lead-in' : ''}
                </Typography>
                {entry.summary && <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.45, mt: 0.5 }}>{entry.summary}</Typography>}
                {Boolean(entry.events?.length) && (
                    <Box component="ul" sx={{ m: 0, mt: 0.75, p: 0, listStyle: 'none', display: 'grid', gap: 0.5 }}>
                        {entry.events.map((event) => (
                            <Box component="li" key={`${event.start}:${event.text}`} sx={{ display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr)', gap: 1, fontSize: '0.72rem', lineHeight: 1.4 }}>
                                <Box component="time" dateTime={event.start} sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{eventDays(event.start, event.end)}</Box>
                                <span>{event.text}</span>
                            </Box>
                        ))}
                    </Box>
                )}
                {Boolean(entry.tags?.length) && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                        {entry.tags.map((tag) => <KitChip key={tag} label={tag} className="!normal-case" />)}
                    </Box>
                )}
            </Box>
        ));
    }
    return (
        <Box sx={{ border: '1px solid', borderColor: (theme) => alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.34 : 0.62), borderRadius: 2, minWidth: 0 }}>
            <Box sx={{ px: 1.5, py: 1 }}>
                <Typography variant="subtitle2" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>Market calendar</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{windowLabel}</Typography>
            </Box>
            <Divider />
            <Box sx={{ px: 1.5, py: 1, maxHeight: 420, overflowY: 'auto' }}>
                {body}
                {!loading && !error && entries.length > VISIBLE_ENTRIES && (
                    <Button size="small" onClick={() => setExpanded(showAll ? null : windowLabel)} sx={{ mt: 0.5 }}>
                        {showAll ? 'Show fewer' : `Show ${entries.length - VISIBLE_ENTRIES} more`}
                    </Button>
                )}
            </Box>
            {sources.length > 0 && (
                <>
                    <Divider />
                    <Typography component="div" variant="caption" color="text.secondary" sx={{ px: 1.5, py: 1, fontSize: '0.65rem', lineHeight: 1.5 }}>
                        Sources:{' '}
                        {sources.map((source, index) => {
                            const label = [source.publisher, source.title].filter(Boolean).join(', ');
                            const dated = source.published_on ? `${label} (${formatDay(source.published_on)})` : label;
                            return (
                                <React.Fragment key={`${source.url || ''}:${index}`}>
                                    {index > 0 && ' · '}
                                    {source.url ? <Link href={source.url} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit">{dated}</Link> : dated}
                                </React.Fragment>
                            );
                        })}
                    </Typography>
                </>
            )}
        </Box>
    );
}

CalendarCard.propTypes = {
    windowLabel: PropTypes.string.isRequired,
    leadInBefore: PropTypes.string,
    loading: PropTypes.bool,
    error: PropTypes.any,
    calendar: PropTypes.shape({ available: PropTypes.bool, entries: PropTypes.array }),
    onRetry: PropTypes.func,
};
